import { CalendarToday, Videocam } from '@mui/icons-material';
import { Badge, Chip } from '@mui/material';

import { formatDateTime } from '../../../utils/formatting';
import { ExplorableId } from '../../explorable-id';
import { bool, num, str } from '../field-access';
import type { ResourceDetailSlotProps } from '../types';
import { statusTone } from './status-tone';

/**
 * Appointment identity band — reads the registry record, so it stays correct
 * without the expansion query having resolved.
 */
export function AppointmentHeader({ data, resourceId, scope }: ResourceDetailSlotProps) {
  const title = str(data, 'title') ?? 'Appointment';
  const startTime = str(data, 'start_time');
  const duration = num(data, 'duration');
  const status = str(data, 'status');
  const telehealth = bool(data, 'telehealth');
  const useJotmeet = bool(data, 'use_jotmeet');

  const telehealthLabel = !telehealth
    ? 'In-person'
    : useJotmeet
      ? 'Telehealth · JotMeet'
      : 'Telehealth · External';

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <CalendarToday className="mr-2 h-6 w-6" />
            {title}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {startTime ? formatDateTime(startTime) : '—'}
            {duration ? ` · ${duration} min` : ''}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <ExplorableId
              label="appt"
              value={resourceId}
              organizationId={scope.organizationId}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Chip
            icon={telehealth ? <Videocam /> : undefined}
            label={telehealthLabel}
            color={telehealth ? 'primary' : 'default'}
            size="small"
          />
          {status && (
            <Badge color={statusTone(status)} className="text-sm">
              {status}
            </Badge>
          )}
          {str(data, 'deleted_at') && (
            <Chip label="Deleted" color="error" size="small" />
          )}
        </div>
      </div>
    </div>
  );
}
